/**
 * Telling Capgo "this bundle booted" (notifyAppReady) — only when it did.
 *
 * Capgo rolls a bundle back when notifyAppReady() doesn't arrive within ~10s
 * of launch. main.tsx used to call it on setTimeout(0) right after
 * render() — before React had rendered anything — so a bundle that threw
 * while rendering still reported ready and was never rolled back (review
 * 2026-10-17).
 *
 * Now <AppReadySignal /> sits inside the top-level ErrorBoundary, after the
 * router. It only mounts if the app shell rendered and committed; a render
 * crash in the shell replaces it with the fallback, so it never mounts. It
 * waits a short settle (the first lazy route usually renders in that time)
 * and checks that the boundary hasn't caught anything since, then reports.
 */
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";

/** Report right after the shell's first commit. A 1.5 s settle (1.3.190)
 *  widened the window in which a backgrounded / paused web view or any early
 *  non-fatal boundary catch could fail Capgo's check and roll a GOOD bundle
 *  back (review 2026-10-17). A render crash in the shell still never mounts
 *  this component, so a broken bundle still rolls back. */
export const APP_READY_SETTLE_MS = 0;

let shellErrored = false;
let notified = false;

/** Called by the top-level ErrorBoundary when it catches. */
export function markAppShellErrored(): void {
  shellErrored = true;
}

export function appShellErrored(): boolean {
  return shellErrored;
}

/** Report the bundle healthy to Capgo, once, unless the shell has errored.
 *  Returns whether it reported. Native only; never throws. */
export async function notifyAppReadyOnce(): Promise<boolean> {
  if (notified || shellErrored || !Capacitor.isNativePlatform()) return false;
  notified = true;
  // Retried: a single failed call used to be final, and Capgo would roll the
  // bundle back for want of one message.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
      await CapacitorUpdater.notifyAppReady();
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[live-update] notifyAppReady failed", err);
      await new Promise((r) => setTimeout(r, 1000));
    }
  }
  return false;
}

/** Mount inside the top-level ErrorBoundary, after the router. */
export function AppReadySignal(): null {
  useEffect(() => {
    const timer = setTimeout(() => {
      void notifyAppReadyOnce();
    }, APP_READY_SETTLE_MS);
    return () => clearTimeout(timer);
  }, []);
  return null;
}

/** Test hook. */
export function __resetAppReadyForTests(): void {
  shellErrored = false;
  notified = false;
}
